<script lang="ts">
import type { Transaction } from '../../lib/types';
import { formatAmountSafe } from '../../lib/formatters';
import { Modal, Badge } from '../common';

type CategorizationSuggestion = {
  transactionId: string;
  description: string;
  amount?: number;
  category: string;
  confidence: number;
  displayName?: string;
  error?: string;
};

type EditedSuggestion = {
  category: string;
  displayName: string;
  createRule: boolean;
};

type Props = {
  suggestions: CategorizationSuggestion[];
  categories: string[];
  onClose: () => void;
  onapply?: (event: CustomEvent<Array<{
    transactionId: string;
    category: string;
    displayName: string;
    createRule: boolean;
  }>>) => void;
};

let { suggestions = [], categories = [], onClose, onapply }: Props = $props();

// Track edits for each suggestion
let edits = $state<Map<string, EditedSuggestion>>(new Map());

// Track "Create Rule for All" checkbox - default to true
let createRuleForAll = $state(true);

// Track previous suggestions to detect when they change
let previousSuggestions = $state<CategorizationSuggestion[]>([]);

// Initialize edits with suggestions - only when suggestions actually change
$effect(() => {
  // Only update if suggestions actually changed (different array reference or length)
  if (suggestions === previousSuggestions ||
      (suggestions.length === previousSuggestions.length && suggestions.length === edits.size)) {
    return;
  }

  previousSuggestions = suggestions;

  const newEdits = new Map<string, EditedSuggestion>();
  suggestions.forEach(s => {
    newEdits.set(s.transactionId, {
      category: s.category,
      displayName: s.displayName || '',
      createRule: createRuleForAll, // Use createRuleForAll as default
    });
  });
  edits = newEdits;
});

// Get edited value or default
function getEdit(transactionId: string): EditedSuggestion {
  return edits.get(transactionId) || {
    category: '',
    displayName: '',
    createRule: false,
  };
}

// Update edit
function updateEdit(transactionId: string, updates: Partial<EditedSuggestion>) {
  const current = getEdit(transactionId);

  // Create a new Map to properly trigger reactivity
  const newEdits = new Map(edits);
  newEdits.set(transactionId, { ...current, ...updates });
  edits = newEdits;
}

// Get confidence badge variant
function getConfidenceBadge(confidence: number): { text: string; variant: 'success' | 'warning' | 'danger' } {
  if (confidence >= 0.8) return { text: 'High', variant: 'success' };
  if (confidence >= 0.6) return { text: 'Medium', variant: 'warning' };
  return { text: 'Low', variant: 'danger' };
}

// Handle "Create Rule for All" toggle
function handleCreateRuleForAllToggle(checked: boolean) {
  createRuleForAll = checked;

  // Create a completely new Map to trigger reactivity
  const newEdits = new Map<string, EditedSuggestion>();
  edits.forEach((edit, transactionId) => {
    newEdits.set(transactionId, { ...edit, createRule: checked });
  });
  edits = newEdits;
}

// Handle accept all
function handleAcceptAll() {
  console.log('[BulkReviewModal] handleAcceptAll called');
  const updates = suggestions
    .filter(s => !s.error)
    .map(s => {
      const edit = getEdit(s.transactionId);
      return {
        transactionId: s.transactionId,
        category: edit.category,
        displayName: edit.displayName,
        createRule: edit.createRule,
      };
    });

  console.log('[BulkReviewModal] Calling onapply with updates:', updates);
  if (onapply) {
    onapply(new CustomEvent('apply', { detail: updates }));
  }
  onClose();
}

// Handle accept high confidence only
function handleAcceptHighConfidence() {
  console.log('[BulkReviewModal] handleAcceptHighConfidence called');
  const updates = suggestions
    .filter(s => !s.error && s.confidence >= 0.8)
    .map(s => {
      const edit = getEdit(s.transactionId);
      return {
        transactionId: s.transactionId,
        category: edit.category,
        displayName: edit.displayName,
        createRule: edit.createRule,
      };
    });

  console.log('[BulkReviewModal] Calling onapply with updates:', updates);
  if (onapply) {
    onapply(new CustomEvent('apply', { detail: updates }));
  }
  onClose();
}

// Handle apply selected
function handleApply() {
  console.log('[BulkReviewModal] handleApply called');
  const updates = suggestions
    .filter(s => !s.error)
    .map(s => {
      const edit = getEdit(s.transactionId);
      return {
        transactionId: s.transactionId,
        category: edit.category,
        displayName: edit.displayName,
        createRule: edit.createRule,
      };
    });

  console.log('[BulkReviewModal] Calling onapply with updates:', updates);
  if (onapply) {
    onapply(new CustomEvent('apply', { detail: updates }));
  }
  onClose();
}

// Computed values
let successfulSuggestions = $derived(suggestions.filter(s => !s.error));
let failedSuggestions = $derived(suggestions.filter(s => s.error));
let highConfidenceCount = $derived(suggestions.filter(s => !s.error && s.confidence >= 0.8).length);
</script>

<Modal
  isOpen={true}
  {onClose}
  title="Review AI Suggestions"
  subtitle="{successfulSuggestions.length} suggestions • {highConfidenceCount} high confidence{failedSuggestions.length > 0 ? ` • ${failedSuggestions.length} failed` : ''}"
  size="xl"
>
  {#snippet children()}
      {#if failedSuggestions.length > 0}
        <div class="failed-section">
          <h3>Failed to Categorize</h3>
          {#each failedSuggestions as suggestion (suggestion.transactionId)}
            <div class="failed-item">
              <span class="description">{suggestion.description}</span>
              <span class="error">{suggestion.error}</span>
            </div>
          {/each}
        </div>
      {/if}

      <div class="suggestions-table">
        <table>
          <thead>
            <tr>
              <th class="col-description">Transaction</th>
              <th class="col-amount">Amount</th>
              <th class="col-category">Suggested Category</th>
              <th class="col-confidence">Confidence</th>
              <th class="col-displayname">Display Name</th>
              <th class="col-rule">
                <div class="rule-header">
                  Create Rule
                  <input
                    type="checkbox"
                    checked={createRuleForAll}
                    onchange={(e) => handleCreateRuleForAllToggle(e.currentTarget.checked)}
                    title="Toggle create rule for all"
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {#each successfulSuggestions as suggestion (suggestion.transactionId)}
              {@const edit = getEdit(suggestion.transactionId)}
              {@const badge = getConfidenceBadge(suggestion.confidence)}
              <tr>
                <td class="col-description">
                  <div class="description-cell">
                    <span class="text" title={suggestion.description}>{suggestion.description}</span>
                  </div>
                </td>
                <td class="col-amount">
                  <span class="amount" class:expense={suggestion.amount < 0} class:income={suggestion.amount >= 0}>
                    {formatAmountSafe(suggestion.amount)}
                  </span>
                </td>
                <td class="col-category">
                  <div class="input-wrapper">
                    <select
                      value={edit.category}
                      onchange={(e) => updateEdit(suggestion.transactionId, {
                        category: e.currentTarget.value
                      })}
                    >
                      {#each categories as cat}
                        <option value={cat}>{cat}</option>
                      {/each}
                    </select>
                  </div>
                </td>
                <td class="col-confidence">
                  <Badge variant={badge.variant}>
                    {badge.text} ({Math.round(suggestion.confidence * 100)}%)
                  </Badge>
                </td>
                <td class="col-displayname">
                  <div class="input-wrapper">
                    <input
                      type="text"
                      value={edit.displayName}
                      oninput={(e) => updateEdit(suggestion.transactionId, {
                        displayName: e.currentTarget.value
                      })}
                      placeholder="Optional display name"
                    />
                  </div>
                </td>
                <td class="col-rule">
                  <div class="checkbox-wrapper">
                    <input
                      type="checkbox"
                      checked={edit.createRule}
                      onchange={(e) => updateEdit(suggestion.transactionId, {
                        createRule: e.currentTarget.checked
                      })}
                    />
                  </div>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
  {/snippet}

  {#snippet footer()}
    <button class="button secondary" onclick={onClose}>
      Cancel
    </button>
    <button
      class="button secondary"
      onclick={handleAcceptHighConfidence}
      disabled={highConfidenceCount === 0}
    >
      Accept High Confidence ({highConfidenceCount})
    </button>
    <button class="button secondary" onclick={handleAcceptAll}>
      Accept All ({successfulSuggestions.length})
    </button>
    <button class="button primary" onclick={handleApply}>
      Apply Changes
    </button>
  {/snippet}
</Modal>

<style lang="scss">
.failed-section {
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-md);
  background: var(--color-danger-light);
  border: 1px solid var(--color-danger);
  border-radius: var(--radius-md);

  h3 {
    margin: 0 0 var(--spacing-md) 0;
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--color-danger);
  }
}

.failed-item {
  display: flex;
  justify-content: space-between;
  padding: var(--spacing-sm);
  font-size: var(--font-size-sm);

  .description {
    font-weight: var(--font-weight-medium);
  }

  .error {
    color: var(--color-text-secondary);
    font-style: italic;
  }
}

.suggestions-table {
  overflow-x: auto;
  overflow-y: auto;
  max-height: calc(85vh - 300px);

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--font-size-sm);
    table-layout: fixed;

    th, td {
      padding: var(--spacing-sm) var(--spacing-md);
      text-align: left;
      border-bottom: 1px solid var(--color-border);
      vertical-align: middle;
    }

    thead {
      background: var(--color-background-secondary);
      font-weight: var(--font-weight-semibold);
      position: sticky;
      top: 0;
      z-index: 2;

      th {
        background: var(--color-background-secondary);
        border-bottom: 2px solid var(--color-border);
        white-space: nowrap;
      }
    }

    tbody tr {
      transition: background var(--transition-fast) ease;

      &:hover {
        background: var(--color-background-tertiary);
      }
    }
  }
}

.col-description {
  width: 30%;
}

.col-amount {
  width: 10%;
  text-align: right;
}

.col-category {
  width: 16%;
}

.col-confidence {
  width: 12%;
}

.col-displayname {
  width: 20%;
}

.col-rule {
  width: 12%;
  text-align: center;

  th {
    text-align: center;
  }
}

.rule-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);

  input[type="checkbox"] {
    cursor: pointer;
    width: 16px;
    height: 16px;
  }
}

.amount {
  font-weight: var(--font-weight-semibold);
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;

  &.expense {
    color: var(--color-text-primary);
  }

  &.income {
    color: var(--color-success);
  }
}

.description-cell {
  .text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: var(--font-weight-medium);
    color: var(--color-text-primary);
  }
}

.input-wrapper {
  width: 100%;
}

.checkbox-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

select, input[type="text"] {
  width: 100%;
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  background: var(--color-background);
  color: var(--color-text-primary);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: var(--shadow-focus);
  }

  &:hover:not(:focus) {
    border-color: color-mix(in srgb, var(--color-border) 70%, var(--color-primary));
  }
}

select {
  cursor: pointer;
  padding-right: var(--spacing-lg);
}

input[type="checkbox"] {
  cursor: pointer;
  width: 18px;
  height: 18px;
}

.button {
  padding: var(--spacing-sm) var(--spacing-xl);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-fast) ease;
  border: none;
  white-space: nowrap;

  &.primary {
    background: var(--color-primary);
    color: white;

    &:hover:not(:disabled) {
      background: color-mix(in srgb, var(--color-primary) 85%, black);
      transform: translateY(-1px);
      box-shadow: var(--shadow-glow-md);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }
  }

  &.secondary {
    background: var(--color-background);
    color: var(--color-text-primary);
    border: 1px solid var(--color-border);

    &:hover:not(:disabled) {
      background: var(--color-background-tertiary);
      border-color: var(--color-primary);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
}
</style>
